import { Gallery, Photo, TimePoint } from "../types";
import config from "../config";
import { Language } from "../translations";

// Construct URL with the correct base URL for the environment
const getUrl = (path: string): string => {
  return `${config.galleryBaseUrl}${path}`;
};

// Construct API URL
const getApiUrl = (path: string): string => {
  return `${config.apiBaseUrl}${path}`;
};

// Note: Event listing has been removed. Galleries/packages are now accessed via share links only.

type GalleryLinkData = {
  id: string;
  i18n: Partial<
    Record<
      Language,
      {
        title: string;
      }
    >
  >;
  coverPhotoUrl: string;
};

type PhotoData = {
  name: string;
  time: string;
  thumbnail?: string;
};

// API Response type from backend
type GalleryApiResponse = {
  id: string;
  i18n: Partial<
    Record<
      Language,
      {
        title: string;
        introParagraph1?: string;
        introParagraph2?: string;
      }
    >
  >;
  defaultLanguage: Language;
  date?: string;
  availableUntilDate?: string;
  zipFile?: string;
  parentId?: string;
  subGalleries: GalleryLinkData[];
  photos: PhotoData[];
  urlPrefix: string;
};

// Fetch photos for a specific event from API
export const fetchGallery = async (
  eventId: string,
): Promise<Gallery | null> => {
  try {
    console.log(`Fetching gallery from API: ${eventId}`);

    const response = await fetch(getApiUrl(`/photos-eu/galleries/${eventId}`));

    if (!response.ok) {
      console.error(
        `Failed to fetch gallery: ${response.status} ${response.statusText}`,
      );
      return null;
    }

    const galleryData: GalleryApiResponse = await response.json();

    // Create photo objects from API data
    const photos: Photo[] = galleryData.photos.map(
      ({ name, time, thumbnail }) => ({
        id: `${name}`,
        eventId,
        url: getUrl(`${galleryData.urlPrefix}/${name}`),
        thumbnailUrl: thumbnail
          ? getUrl(`${galleryData.urlPrefix}/${thumbnail}`)
          : getUrl(`${galleryData.urlPrefix}/${name}`),
        takenAt: time,
        width: 1200, // These would ideally come from image metadata
        height: 800,
      }),
    );

    // Sort by time taken
    photos.sort(
      (a, b) => new Date(a.takenAt).getTime() - new Date(b.takenAt).getTime(),
    );
console.log('!!!', galleryData, photos[0])
    return {
      id: eventId,
      i18n: galleryData.i18n,
      defaultLanguage: galleryData.defaultLanguage,
      date: galleryData.date,
      availableUntilDate: galleryData.availableUntilDate,
      ...(galleryData.zipFile
        ? {
            zipFileUrl: getUrl(
              `${galleryData.urlPrefix}/${galleryData.zipFile}`,
            ),
          }
        : {}),
      parentId: galleryData.parentId,
      subGalleries: galleryData.subGalleries,
      photos,
    };
  } catch (error) {
    console.error(`Error fetching photos for event ${eventId}:`, error);
    return null;
  }
};

// Generate time points for timeline (only for hours with photos)
export const generateTimePoints = (photos: Photo[]): TimePoint[] => {
  if (photos.length === 0) return [];

  // Helper to format date in local time consistently
  const formatLocalDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hour = date.getHours().toString().padStart(2, "0");
    return `${year}-${month}-${day} ${hour}:00`;
  };

  const timePoints: TimePoint[] = [];
  const firstPhoto = new Date(photos[0].takenAt);
  const lastPhoto = new Date(photos[photos.length - 1].takenAt);

  // Map to track hours that have photos (as date strings in local time)
  const hoursWithPhotos = new Set<string>();

  // Identify which hours have photos
  photos.forEach((photo) => {
    const photoDate = new Date(photo.takenAt);
    hoursWithPhotos.add(formatLocalDateTime(photoDate));
  });

  // Set start time to the beginning of the hour
  const startHour = new Date(firstPhoto);
  startHour.setMinutes(0, 0, 0);

  // Create time points only for hours that have photos
  const currentHour = new Date(startHour);
  while (currentHour <= lastPhoto) {
    // Format as date+time to ensure uniqueness across multiple days (using local time)
    const fullDateTime = formatLocalDateTime(currentHour);

    // Only add time point if there are photos in this hour
    if (hoursWithPhotos.has(fullDateTime)) {
      const displayTime = `${currentHour.getHours().toString().padStart(2, "0")}:00`;

      // Find position of first photo after this time
      const position = photos.findIndex(
        (photo) => new Date(photo.takenAt) >= currentHour,
      );

      timePoints.push({
        time: displayTime,
        fullDateTime: fullDateTime, // Store full date+time for unique key
        isoDateTime: currentHour.toISOString(),
        position: position !== -1 ? position : photos.length - 1,
      });
    }

    // Move to next hour
    currentHour.setHours(currentHour.getHours() + 1);
  }

  return timePoints;
};
